import './styles.scss';

import React, { memo, useState } from 'react';

import { Button, message } from 'antd';
import { AxiosError } from 'axios';
import TMIcon from 'tm-icons-library';

import API from '../../../../api';
import showAppError from '../../../../shared/error';
import {
  AcceptedFileType,
  uploadFiles,
  uploadFilesHandler,
} from '../../../../shared/file';
import {
  AllowedFileTypeInQuestion,
  IQuestionAnswerAttachment,
} from '../../../../types/assignmentTypes';
import { IFileUploadAsset } from '../../../../types/communityTypes';
import { EUploadModuleType } from '../../../../types/fileTypes';
import { useAssignmentContext } from '../../utils/AssignmentProvider';

const ACCEPT_FILE_TYPES = {
  image: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
  video: ['mp4', 'mov'],
  docx: ['docx', 'doc'],
  pdf: ['pdf'],
};

interface Props {
  questionId: string;
  attachments: IQuestionAnswerAttachment[];
  maxFileCount: number;
  maxFileSize: number; // in bytes
  allowedFileTypes: AllowedFileTypeInQuestion[];
  subjectiveAnswer: string;
}

const AssignmentAttachments: React.FC<Props> = ({
  questionId,
  attachments,
  maxFileCount,
  maxFileSize, // in bytes
  allowedFileTypes,
  subjectiveAnswer,
}) => {
  const { assignment, flow, saveQuestionAnswer } = useAssignmentContext();

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const uploadAttachments = async (files: File[]) => {
    if (!assignment?._id || files.length === 0) return;

    setIsUploading(true);

    try {
      // const formData = new FormData();

      // if (files.length > 0) {
      //   files.forEach((file) => {
      //     formData.append('file[]', file);
      //   });
      // }

      // const resp = await AssignmentAPI.uploadAssignmentQuestionAttachments(
      //   assignment._id,
      //   questionId,
      //   formData,
      //   (event) => {
      //     if (typeof event.progress === 'number') {
      //       setUploadProgress(event.progress);
      //     }
      //   },
      // );

      const resp = await API.uploadFileInAssetsV2(
        files,
        EUploadModuleType.COURSE_ASSIGNMENT_SUBMISSION,
        `assignmentId=${assignment?._id}&questionId=${questionId}`,
      );

      if ([200, 201].includes(resp.status)) {
        const result = resp.data.result.assets.map(
          (asset: IFileUploadAsset) => ({
            url: asset.url,
            name: asset.fileName,
            type: asset.fileType as any,
            size: asset.fileSize,
            mimeType: asset.contentType,
            key: new URL(asset.url).pathname,
            _id: asset.assetId,
          }),
        );

        await saveQuestionAnswer(questionId, 'save', {
          subjectiveAnswer,
          attachments: [...attachments, ...result],
        });
      } else {
        showAppError(resp.data.message);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        showAppError(error.response?.data.message);
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const getSingularOrPlural = (count: number, singular: string) => {
    return count === 1 ? singular : `${singular}s`;
  };

  const formatFileSize = (size: number) => {
    if (size / 1024 > 1024) {
      return `${(size / 1024 / 1024).toFixed(2)} MB`;
    }
    return `${(size / 1024).toFixed(2)} KB`;
  };

  const handleFileUpload = () => {
    uploadFilesHandler({
      type: 'doc',
      mode: 'multiple',
      accept: allowedFileTypes.flatMap((type) =>
        ACCEPT_FILE_TYPES[type] ? ACCEPT_FILE_TYPES[type] : [],
      ) as AcceptedFileType[],
      onUpload: (items, type) => {
        uploadFiles({
          files: items,
          type,
          maxSizeUnit: 'MB',
          // convert bytes to MB
          maxSize: maxFileSize / (1024 * 1024), // convert bytes to MB
          uploadKey: EUploadModuleType.COURSE_ASSIGNMENT_QUESTION,
          uploadCallback: (list) => {
            const newFiles = [...list];
            const remainingFilesCount = maxFileCount - newFiles.length;

            if (
              newFiles.length > 0 &&
              remainingFilesCount > 0 &&
              newFiles.length > remainingFilesCount
            ) {
              message.warning(
                `Maximum ${maxFileCount} attachments allowed. Only the first ${remainingFilesCount} will be uploaded.`,
              );
              uploadAttachments(newFiles.slice(0, remainingFilesCount));
              return;
            }

            uploadAttachments(newFiles);
          },
        });
      },
    });
  };

  const handleRemoveAttachment = (key: string) => {
    saveQuestionAnswer(questionId, 'save', {
      subjectiveAnswer,
      attachments: attachments.filter((item) => item.key !== key),
    });
  };

  return (
    <div className="assignment-attachments">
      <div className="assignment-attachments__content">
        {flow === 'submit' ? (
          <div className="assignment-attachments__header">
            <span className="assignment-attachments__title">
              Add {getSingularOrPlural(maxFileCount || 0, 'attachment')} (
              {attachments.length}/{maxFileCount || 0})
            </span>
            <span className="assignment-attachments__subtitle">
              Max file size: {maxFileSize / 1024 / 1024} MB (per file)
            </span>
          </div>
        ) : null}

        {uploadProgress > 0 && (
          <div className="assignment-attachments__progress">
            <div className="assignment-attachments__progress-bar">
              <div
                className="assignment-attachments__progress-fill"
                style={{ width: `${uploadProgress * 100}%` }}
              />
            </div>
          </div>
        )}

        <div className="assignment-attachments__files">
          {attachments.map(({ key, url, name, size }) => (
            <div key={`file-${key}`} className="assignment-attachments__file">
              <div className="assignment-attachments__file-content">
                <div className="assignment-attachments__file-icon">
                  <TMIcon name="document-text" size={20} color="#fff" />
                </div>
                <div className="assignment-attachments__file-info">
                  <span className="assignment-attachments__file-name">
                    {name}
                  </span>
                  <span className="assignment-attachments__file-details">
                    {name?.split('.')?.pop()?.toUpperCase()}{' '}
                    {formatFileSize(size)}
                  </span>
                </div>
                {flow === 'review' ? (
                  <div className="assignment-attachments__file-status">
                    <TMIcon
                      name="download-outline"
                      size={16}
                      color="#fff"
                      onClick={() => window.open(url, '_blank')}
                    />
                  </div>
                ) : (
                  <button
                    className="assignment-attachments__file-remove"
                    onClick={() => handleRemoveAttachment(key)}>
                    <TMIcon name="trash-bin-outline" size={22} color="#666" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {flow === 'submit' ? (
          <Button
            className="siteBtn siteBtnSecondary assignment-attachments__upload-btn"
            icon={
              <TMIcon
                name="cloud-upload-outline"
                size={16}
                color="#000"
                style={{ marginRight: 4 }}
              />
            }
            disabled={isUploading}
            loading={isUploading}
            onClick={() => {
              if (attachments.length >= maxFileCount) {
                message.warning(
                  `You can only select upto ${maxFileCount} attachment(s)`,
                );
              } else {
                handleFileUpload();
              }
            }}>
            Click to upload
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default memo(AssignmentAttachments);
